import { useState, useMemo, useEffect } from "react";
import { getFirestore, doc, deleteDoc } from "firebase/firestore";
import moment from "moment";
import Swal from "sweetalert2";
import ModalCreateEvent from "./ModalCreateEvent";
import PreviewDayModal from "./PreviewDayModal";

const VistaMes = ({
  events,
  currentDate,
  onSave,
  filters,
  handleAddCategory,
  refreshData,
  handleToday,
}) => {
  const [previewEvent, setPreviewEvent] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    event: null,
  });
  const MAX_LANES_VISIBLE = 3;
  const [listModal, setListModal] = useState({
    open: false,
    events: [],
    x: 0,
    y: 0,
  });

  const handleMoreClick = (hiddenEvents, e) => {
    e.stopPropagation(); // evita que se cierre el contexto general

    const POP_W = 240; // ancho fijo del pop-over
    const POP_ITEM_H = 28; // alto aprox. de cada fila de evento
    const POP_HEAD_H = 32; // alto del encabezado (fecha + botón cerrar)
    const PADDING = 8; // espacio alrededor del cursor

    const { clientX, clientY } = e;

    // Posición inicial a la derecha-abajo del cursor
    let x = clientX + PADDING;
    let y = clientY + PADDING;

    // Alto real del pop-over según nº de eventos
    const popH = POP_HEAD_H + hiddenEvents.length * POP_ITEM_H;

    // Si no cabe a la derecha, lo colocamos a la izquierda del cursor
    if (x + POP_W > window.innerWidth) {
      x = clientX - POP_W - PADDING;
    }

    // Si no cabe abajo, lo desplazamos hacia arriba (sin salirse de la pantalla)
    if (y + popH > window.innerHeight) {
      y = Math.max(PADDING, window.innerHeight - popH - PADDING);
    }

    setListModal({ open: true, events: hiddenEvents, x, y });
  };

  useEffect(() => {
    const handleGlobalClick = () => {
      // cerrar la lista si está abierta
      setListModal((prev) => (prev.open ? { ...prev, open: false } : prev));
      // cerrar el menú contextual si está visible
      setContextMenu((prev) =>
        prev.visible ? { visible: false, x: 0, y: 0, event: null } : prev
      );
    };

    window.addEventListener("click", handleGlobalClick);
    return () => window.removeEventListener("click", handleGlobalClick);
  }, [listModal.open, contextMenu.visible]);

  const handleDeleteClick = async (event) => {
    // Calculamos el ancho real del scrollbar
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    // Si hay scrollbar, ajustamos paddingRight en función de su ancho
    if (scrollbarWidth > 0) {
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = "0";
    }

    const firestore = getFirestore();
    const confirm = await Swal.fire({
      title: "¿Eliminar evento?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (confirm.isConfirmed) {
      try {
        if (event.resource === "evento") {
          await deleteDoc(
            doc(firestore, "eventos", event.id.replace("evento-", ""))
          );
        } else if (event.resource === "tarea") {
          await deleteDoc(
            doc(firestore, "tareaCalendario", event.id.replace("tarea-", ""))
          );
        }
        Swal.fire(
          "Eliminado",
          "El evento ha sido eliminado con éxito.",
          "success"
        );

        setTimeout(() => {
          if (refreshData) refreshData();
          document.body.style.overflow = "auto";
          document.body.style.paddingRight = "0";
        }, 100);
      } catch (error) {
        console.error("Error al eliminar:", error);
        Swal.fire("Error", "No se pudo eliminar el evento.", "error");
        // Restauramos en caso de error
        document.body.style.overflow = "auto";
        document.body.style.paddingRight = "0";
      }
    } else {
      // Si se canceló el SweetAlert
      document.body.style.overflow = "auto";
      document.body.style.paddingRight = "0";
    }
  };

  const handleEditClick = (event) => {
    setEventToEdit({
      ...event,
      note: event.note || "", // <<< aquí
      start: moment(event.start).format("YYYY-MM-DDTHH:mm"),
      end: moment(event.end).format("YYYY-MM-DDTHH:mm"),
    });
    setEditModalOpen(true);
  };

  const daysInMonth = currentDate.daysInMonth();
  const startOfMonth = (currentDate.clone().startOf("month").day() + 6) % 7;
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const daysOfWeek = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  const weeks = useMemo(() => {
    const allCells = [...Array(startOfMonth).fill(null), ...daysArray];
    const result = [];
    for (let i = 0; i < allCells.length; i += 7) {
      result.push(allCells.slice(i, i + 7));
    }
    return result;
  }, [startOfMonth, daysArray]);

  const openEventModal = (ev) => setPreviewEvent(ev);

  const closePreview = () => setPreviewEvent(null);

  const handleEventClick = (ev, e, spanCols, colStart, week) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const dayWidth = rect.width / spanCols;
    const idx = Math.floor(clickX / dayWidth);

    const dayNum = week[colStart + idx];
    if (!dayNum) return;

    const newStart = moment(ev.start).clone().date(dayNum);
    const newEnd = moment(ev.end);

    openEventModal({
      ...ev,
      start: newStart.toDate(),
      end: newEnd.toDate(),
      note: ev.note,
    });
  };

  const renderWeekEvents = (
    week,
    currentDate,
    daysInMonth,
    events,
    handleEventClick
  ) => {
    /* 1️⃣  Eventos que tocan la semana */
    const weekStart = currentDate
      .clone()
      .date(week[0] || 1)
      .startOf("day");
    const weekEnd = currentDate
      .clone()
      .date(week[6] || daysInMonth)
      .endOf("day");

    const weekEvents = events
      .filter(
        (ev) =>
          moment(ev.start).isBefore(weekEnd) &&
          moment(ev.end).isAfter(weekStart)
      )
      .sort((a, b) => {
        const spanA = moment(a.end)
          .startOf("day")
          .diff(moment(a.start).startOf("day"), "days");
        const spanB = moment(b.end)
          .startOf("day")
          .diff(moment(b.start).startOf("day"), "days");

        if (spanA === 0 && spanB > 0) return 1;
        if (spanB === 0 && spanA > 0) return -1;
        if (spanB !== spanA) return spanB - spanA;
        return moment(a.start).diff(b.start);
      });

    /* 2️⃣  Calculamos lane y columnas */
    const positioned = []; // { ev, si, ei, lane }
    const lanes = [];

    weekEvents.forEach((ev) => {
      const s = moment.max(moment(ev.start), weekStart);
      const e = moment.min(moment(ev.end), weekEnd);
      const si = week.findIndex((d) => d === s.date());
      const ei = week.findIndex((d) => d === e.date());

      // 🌟 Para pintar, sólo usamos su rango real:
      const rangeStart = s.clone().startOf("day"); // SIEMPRE día completo
      const rangeEnd = e.clone().endOf("day");

      let lane = lanes.findIndex((row) =>
        row.every(
          (o) => !(rangeStart.isBefore(o.end) && rangeEnd.isAfter(o.start))
        )
      );
      if (lane === -1) {
        lanes.push([]);
        lane = lanes.length - 1;
      }
      lanes[lane].push({ start: rangeStart, end: rangeEnd });
      positioned.push({ ev, si, ei, lane });
    });

    /* 3️⃣  Barras visibles (≤ MAX_LANES_VISIBLE) */
    const elements = [];
    positioned.forEach(({ ev, si, ei, lane }) => {
      if (lane >= MAX_LANES_VISIBLE) return;
      const colStart = Math.max(si, 0);
      const spanCols = (ei >= 0 ? ei : 6) - colStart + 1;
      const leftPct = (colStart / 7) * 100;
      const widthPct = (spanCols / 7) * 100;
      const topPx = 30 + lane * 32;

      elements.push(
        // ... dentro de tu map de elementos de evento:
        <div
          key={ev.id}
          className="absolute z-10 text-xs font-medium cursor-pointer"
          style={{
            top: `${topPx}px`,
            left: `calc(${leftPct}% + 0.5rem)`,
            width: `calc(${widthPct}% - 1rem)`,
            height: "1.75rem",
            backgroundColor: ev.color,
            padding: "0 0.5rem",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxSizing: "border-box",
          }}
          onClick={(e) => handleEventClick(ev, e, spanCols, colStart, week)}
          onContextMenu={(e) => {
            e.preventDefault();
            // dimensiones del menú contextual
            const MENU_W = 150; // ancho fijo
            const MENU_H = 2 * 32; // dos opciones × 32px alto cada una
            const PADDING = 8;

            let x = e.clientX + PADDING;
            let y = e.clientY + PADDING;

            // ajusta si rebasamos el borde derecho
            if (x + MENU_W > window.innerWidth) {
              x = e.clientX - MENU_W - PADDING;
            }
            // ajusta si rebasamos el borde inferior
            if (y + MENU_H > window.innerHeight) {
              y = Math.max(PADDING, window.innerHeight - MENU_H - PADDING);
            }

            setContextMenu({
              visible: true,
              x,
              y,
              event: ev,
            });
          }}
        >
          <span className="truncate">{ev.title}</span>
          <span className="ml-auto text-[0.65rem] font-semibold">
            {moment(ev.start).format("HH:mm")}
          </span>
          {moment(ev.end).format("HH:mm") !==
            moment(ev.start).format("HH:mm") && (
            <span className="ml-2 text-[0.65rem] font-semibold">
              {moment(ev.end).format("HH:mm")}
            </span>
          )}
        </div>
      );
    });

    /* 4️⃣  Agrupamos los ocultos por rango y mostramos siempre “+N” */
    const hiddenRanges = {};
    positioned
      .filter(({ lane }) => lane >= MAX_LANES_VISIBLE)
      .forEach(({ si, ei, ev }) => {
        const key = `${Math.max(si, 0)}-${ei >= 0 ? ei : 6}`;
        (hiddenRanges[key] ||= []).push(ev);
      });

    Object.entries(hiddenRanges).forEach(([range, evArr]) => {
      const [si, ei] = range.split("-").map(Number);
      const leftPct = (si / 7) * 100;
      const widthPct = ((ei - si + 1) / 7) * 100;
      const topPx = 30 + MAX_LANES_VISIBLE * 32;

      elements.push(
        <div
          key={`more-${range}`}
          className="absolute z-20 text-[10px] font-semibold cursor-pointer hover:bg-gray-100"
          style={{
            top: `${topPx}px`,
            left: `calc(${leftPct}% + 0.5rem)`,
            width: `calc(${widthPct}% - 1rem)`,
            height: "1.75rem",
            backgroundColor: "#EEF2FF",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#1D4ED8",
          }}
          onClick={(e) => handleMoreClick(evArr, e)}
        >
          +{evArr.length}
        </div>
      );
    });

    /* 5️⃣  Monodía: +N si superan los 3 visibles en la misma casilla */
    week.forEach((_, dayIdx) => {
      const hiddenSingleDay = positioned.filter(
        (p) => p.lane >= MAX_LANES_VISIBLE && p.si === p.ei && p.si === dayIdx
      );
      if (hiddenSingleDay.length <= MAX_LANES_VISIBLE - 1) return;

      const leftPct = (dayIdx / 7) * 100;
      const widthPct = 100 / 7;
      const topPx = 30 + MAX_LANES_VISIBLE * 32;

      elements.push(
        <div
          key={`single-more-${dayIdx}`}
          className="absolute z-20 text-[10px] font-semibold cursor-pointer hover:bg-gray-100"
          style={{
            top: `${topPx}px`,
            left: `calc(${leftPct}% + 0.5rem)`,
            width: `calc(${widthPct}% - 1rem)`,
            height: "1.75rem",
            backgroundColor: "#EEF2FF",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#1D4ED8",
          }}
          onClick={(e) =>
            handleMoreClick(
              hiddenSingleDay.map((h) => h.ev),
              e
            )
          }
        >
          +{hiddenSingleDay.length}
        </div>
      );
    });

    return elements;
  };

  return (
    <>
      <div className="bg-gray-50 h-auto w-full">
        <div className="mx-auto bg-white max-w-[1600px] w-full overflow-hidden">
          {/* Encabezado de los días de la semana */}
          <div
            className="grid grid-cols-7 text-xs text-gray-500 text-center p-2"
            style={{ borderBottom: "1px solid #E6EDFF" }}
          >
            {daysOfWeek.map((day) => (
              <div key={day}>{day}</div>
            ))}
          </div>

          {/* --- Calendario por semanas con barras multidía --- */}
          <div className="w-full overflow-hidden">
            {weeks.map((week, wi) => (
              <div
                key={wi}
                className="relative grid grid-cols-7 min-h-[10rem] overflow-hidden"
                style={{ borderBottom: "1px solid #E6EDFF" }}
              >
                {/* Celdas de día */}
                {week.map((day, di) => {
                  const isToday = day
                    ? moment().isSame(currentDate.clone().date(day), "day")
                    : false;

                  return (
                    <div
                      key={di}
                      style={{
                        backgroundColor: isToday ? "#F8FAFB" : "transparent",
                        borderRight: "1px solid #E6EDFF",
                      }}
                      className="p-2 text-xs text-gray-700 cursor-pointer"
                      onClick={(e) => {
                        if (!day) return;
                        e.stopPropagation();

                        // 📅 Fecha que se clickeó (momento a las 00:00)
                        const clickDate = currentDate.clone().date(day);

                        // 🔍 Comprueba si ya hay eventos ese día
                        const dayEvents = events.filter((ev) =>
                          moment(clickDate).isBetween(
                            ev.start,
                            ev.end,
                            "day",
                            "[]"
                          )
                        );

                        if (dayEvents.length > 0) {
                          handleMoreClick(dayEvents, e);
                        } else {
                          // ⏰ Hora sugerida: ahora mismo redondeada a 5 min
                          const now = moment();
                          const start = clickDate
                            .hour(now.hour())
                            .minute(Math.floor(now.minute() / 5) * 5)
                            .second(0);
                          const end = start.clone().add(1, "hour");

                          setEventToEdit({
                            id: null,
                            title: "",
                            note: "",
                            start: start.toISOString(),
                            end: end.toISOString(),
                            resource: "evento",
                          });
                          setEditModalOpen(true);
                        }
                      }}
                    >
                      {day && (
                        <span
                          className={
                            isToday
                              ? "inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white font-semibold"
                              : "font-semibold text-gray-700"
                          }
                        >
                          {day}
                        </span>
                      )}
                    </div>
                  );
                })}

                {/* Barras de eventos multidía */}
                {renderWeekEvents(
                  week,
                  currentDate,
                  daysInMonth,
                  events,
                  handleEventClick
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {listModal.open && (
        <div
          className="fixed bg-white rounded-lg shadow-lg p-2 z-50"
          style={{
            top: listModal.y + 8,
            left: listModal.x + 8,
            width: 240,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-sm font-medium mb-2">
            {moment(listModal.events[0].start).format("LL")}
            <button
              onClick={() => setListModal({ ...listModal, open: false })}
              className="float-right text-gray-400 hover:text-gray-600"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          {/*  ▼▼  FILAS CLICABLES  ▼▼  */}
          {listModal.events.map((ev) => (
            <div
              key={ev.id}
              className="flex items-center text-sm mb-1 rounded-full px-2 py-1 cursor-pointer"
              style={{ backgroundColor: ev.color || "#EEE" }}
              onClick={(e) => {
                e.stopPropagation(); // no cierres “de rebote”
                setListModal({ ...listModal, open: false }); // cierra el pop-over
                openEventModal(ev); // muestra el modal grande
              }}
            >
              <span className="flex-1 truncate">{ev.title}</span>
            </div>
          ))}
        </div>
      )}

      {/* Modal con la lista de eventos del día */}
      <PreviewDayModal
        isOpen={!!previewEvent}
        event={previewEvent}
        onClose={closePreview}
      />

      {/* Modal para editar/crear evento */}
      <ModalCreateEvent
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        initialData={eventToEdit}
        isNew={!eventToEdit?.id}
        onSave={onSave}
        categories={filters}
        onAddCategory={handleAddCategory}
      />

      {contextMenu.visible && (
        <div
          className="fixed bg-white border border-gray-300 shadow-lg z-50 divide-y divide-gray-200 rounded-lg overflow-hidden w-36"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={() =>
            setContextMenu({ visible: false, x: 0, y: 0, event: null })
          }
          onContextMenu={(e) => e.preventDefault()}
        >
          <button
            type="button"
            className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-100 focus:outline-none"
            onClick={() => {
              handleEditClick(contextMenu.event);
              setContextMenu({ visible: false, x: 0, y: 0, event: null });
            }}
          >
            <i className="bi bi-pencil-fill mr-2"></i>
            Editar
          </button>

          <button
            type="button"
            className="block w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-gray-100 focus:outline-none"
            onClick={() => {
              handleDeleteClick(contextMenu.event);
              setContextMenu({ visible: false, x: 0, y: 0, event: null });
            }}
          >
            <i className="bi bi-trash-fill mr-2"></i>
            Eliminar
          </button>
        </div>
      )}
    </>
  );
};

export default VistaMes;
